// DataTable.js
import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  TextField,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Stack,
  Chip,
  Skeleton
} from '@mui/material';
import { SearchIcon } from '../../../assets/CommonSvg';
import FilterIcon from '../../../assets/images/icons/filter.icon';
import ArrowDropIcon from '../../../assets/images/icons/arrow-drop.icon';
import PopoverDialog from '../../common/PopoverDialog';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import NoDataCard from '../../NoDataCard';
import CloseIcon from '@mui/icons-material/Close';

const DEFAULT_ROWS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 300;

const DataTable = ({
  columns,
  data,
  title,
  remote = false,
  onServerSideChange,
  FilterComponent,
  BtnComponent,
  className,
  searchPlaceholder,
  filterConfig = {},
  loading = false
}) => {
  // State management with proper initial values
  const [items, setItems] = useState(() => data?.items);
  const [page, setPage] = useState(1);
  const [per_page, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortConfig, setSortConfig] = useState({ sort_by: '', sort_order: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [filters, setFilters] = useState(null);
  // Reset page when data changes
  useEffect(() => {
    setItems(data?.items);
    // setPage(1);
  }, [data?.items]);
  // Optimized server-side data fetching
  useEffect(() => {
    if (!remote || !onServerSideChange) return;

    const params = {
      page,
      per_page,
      sort_by: sortConfig.sort_by,
      sort_order: sortConfig.sort_order,
      search: searchQuery,
      ...(filters || {})
    };
    onServerSideChange(params);
  }, [page, per_page, sortConfig, searchQuery, filters]);

  // Optimized search implementation
  const handleSearch = useCallback((query) => {
    if (!remote && query !== searchQuery) {
      const lowercaseQuery = query?.toLowerCase();
      const filteredData = data?.items.filter((row) =>
        columns?.some((column) => {
          const value = column?.selector(row);
          return value?.toString().toLowerCase().includes(lowercaseQuery);
        })
      );
      setItems(filteredData);
      setPage(1);
    }
    setSearchQuery(query);
  }, [data?.items, columns, remote, searchQuery]);

  // Memoized debounced search
  const debouncedSearch = useMemo(
    () => debounce(handleSearch, DEBOUNCE_DELAY),
    [handleSearch]
  );

  // Optimized sort handling
  const handleSort = useCallback((columnId) => {
    setSortConfig((prevConfig) => ({
      sort_by: columnId,
      sort_order: prevConfig?.sort_by === columnId && prevConfig?.sort_order === 'asc' ? 'desc' : 'asc'
    }));

    if (!remote) {
      setItems((currentItems) => {
        const sortedData = [...currentItems].sort((a, b) => {
          const aValue = a[columnId];
          const bValue = b[columnId];

          if (aValue === bValue) return 0;
          if (aValue === null) return 1;
          if (bValue === null) return -1;

          return aValue < bValue ? -1 : 1;
        });

        return sortConfig.sort_order === 'asc' ? sortedData : sortedData.reverse();
      });
    }
  }, [remote, sortConfig.sort_order]);

  // Memoized displayed rows calculation
  const displayedRows = useMemo(() => {
    if (remote) return items;
    const start = (page - 1) * per_page;
    return items?.slice(start, start + per_page);
  }, [items, page, per_page, remote]);

  // Event handlers
  const handleChangePage = (_, newPage) => setPage(newPage + 1);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };
  const handleFilterClick = (event) => setAnchorEl(event.currentTarget);
  const handleFilterClose = () => setAnchorEl(null);
  const handleFilterApply = (params) => {
    setFilters(params);
    handleFilterClose();
  };

  const handleFilterClear = (params) => {
    setFilters(params);
  };

  // Filter Chips Logic
  const getFilterChips = (filters, filterConfig) => {
    const chips = [];
    if (!filters) return chips;

    Object.entries(filters).forEach(([key, value]) => {
      const config = filterConfig[key];


      if (value) {
        if (Array.isArray(value)) {
          value.forEach(val => {
            const label = config?.enum?.[val] || val;

            chips.push({ key, value: val, filterLabel: config?.label, filterValLabel: label });
          });
        } else {
          chips.push({ key, value, filterLabel: config?.label, filterValLabel: value });
        }
      }
    });
    return chips;
  };

  const filterChips = useMemo(() => getFilterChips(filters, filterConfig), [filters, filterConfig]);

  const handleRemoveFilterFromChip = (chipToDelete) => {
    const updatedFilters = { ...filters };
    if (Array.isArray(updatedFilters[chipToDelete.key])) {
      updatedFilters[chipToDelete.key] = updatedFilters[chipToDelete.key].filter(val => val !== chipToDelete.value);
      if (updatedFilters[chipToDelete.key].length === 0) delete updatedFilters[chipToDelete.key];
    } else {
      delete updatedFilters[chipToDelete.key];
    }
    setFilters(updatedFilters);
  };

  const handleClearAllFilters = () => {
    setFilters(null);
  };


  // Security: Memoized sanitizer
  const sanitizeValue = useCallback((value) => {
    if (typeof value === 'string') {
      return value.replace(/[<>]/g, '');
    }
    return value;
  }, []);
  return (
    <Paper elevation={3} className={className} sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none' }}>
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          <Box display="flex">
            <TextField
              variant="outlined"
              className="bg-[#F0F1F5] w-[325px] py-[2.5px] border-0 rounded-[10px]"
              size="small"
              placeholder={searchPlaceholder || "Search by Name/ ID/ Product/ Hospital"}
              sx={{ "& fieldset": { border: 'none' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon className="text-gray-500" />
                  </InputAdornment>
                )
              }}
              onChange={(e) => debouncedSearch(e.target.value)}
            />
            {BtnComponent}
            {FilterComponent && (
              <Stack>
                <IconButton
                  disableRipple
                  aria-label="filter"
                  className="bg-gray-200 h-10 w-10 ml-4 rounded-[10px]"
                  onClick={handleFilterClick}
                >
                  <FilterIcon />
                </IconButton>
                <PopoverDialog
                  title="Filter"
                  anchorEl={anchorEl}
                  onClose={handleFilterClose}
                >
                  <FilterComponent onApply={handleFilterApply} onClear={handleFilterClear} initialFilters={filters} />
                </PopoverDialog>
              </Stack>
            )}
          </Box>
        </Box>

        <Box>
          {filterChips.length > 0 && (
            <Box mt={2} mb={1} display="flex" flexWrap="wrap" gap={1}>
              {filterChips.map((chip, idx) => (
                <Chip
                  key={`${chip.key}-${chip.value}-${idx}`}
                  label={
                    <span className='font-roboto font-normal text-[13px] leading-[100%]'>
                      <span className='!text-[#828282]'>{chip.filterLabel}:</span>{' '}
                      <span className='!text-black'>{chip.filterValLabel}</span>
                    </span>
                  }
                  onDelete={() => handleRemoveFilterFromChip(chip)}
                  deleteIcon={
                    <CloseIcon
                      className="text-[#828282] hover:text-black"
                      style={{ fontSize: 16, marginLeft: 4 }}
                    />
                  }
                  className="!bg-[#F0F1F5]"
                />
              ))}
              <Chip
                label="Clear All"
                onClick={handleClearAllFilters}
                className="!bg-[#3571FF] !text-[#ffffff] hover:!bg-[#171928] font-roboto font-medium text-[13px] leading-[100%] tracking-[0]"
              />
            </Box>
          )}
        </Box>

        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns?.map((column, ind) => (
                  <TableCell
                    key={`header-${column.id}`} // ✅ unique key
                    sortDirection={sortConfig.sort_by === column?.id ? sortConfig.sort_order : false}
                    className="py-2"
                    align={column?.align || 'left'}
                  >
                    {column?.sortable ? (
                      <TableSortLabel
                        key={ind}
                        active={sortConfig.sort_by === column?.id}
                        direction={sortConfig.sort_by === column?.id ? sortConfig.sort_order : 'desc'}
                        onClick={() => handleSort(column?.id)}
                        IconComponent={() => <ArrowDropIcon flip={sortConfig.sort_order === 'desc'} />}
                        className="gap-2"
                        hideSortIcon
                      >
                        {column?.name}
                      </TableSortLabel>
                    ) : (
                      column?.name
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(per_page)].map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {columns?.map((col, colIndex) => (
                      <TableCell key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" width="80%" height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : displayedRows?.length > 0 ? (
                displayedRows.map((row, rowIndex) => (
                  <TableRow hover key={`row-${rowIndex}`}>
                    {columns?.map((column, ind) => (
                      <TableCell
                        key={`cell-${rowIndex}-${ind}`}
                        align={column?.align || 'left'}
                      >
                        {sanitizeValue(column?.selector(row)) ?? "N/A"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns?.length} align="center">
                    <div className="mt-4">
                      <NoDataCard />
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={remote ? (data?.total_count ?? 0) : (items?.length ?? 0)}
          rowsPerPage={per_page}
          page={page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Paper>
  );
};

// PropTypes definition
DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      selector: PropTypes.func.isRequired,
      sortable: PropTypes.bool
    })
  ).isRequired,
  data: PropTypes.shape({
    items: PropTypes.array.isRequired,
    total_count: PropTypes.number.isRequired
  }).isRequired,
  title: PropTypes.string.isRequired,
  remote: PropTypes.bool,
  onServerSideChange: PropTypes.func,
  FilterComponent: PropTypes.elementType,
  className: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  BtnComponent: PropTypes.elementType
};

export default memo(DataTable);